import { TableGridMode } from "consts/table";

import transactions from 'config/filters/transactions.json';
import transactionList from 'config/filters/transactionList.json';
import merchants from 'config/filters/merchants.json';
import users from 'config/filters/users.json';
import merchantPayoutSummaryFiles from 'config/filters/merchantPayouts/summaryFiles.json';
import merchantPayoutFiles from 'config/filters/merchantPayouts/files.json';
import merchantPayoutCalculations from 'config/filters/merchantPayouts/calculations.json';
import merchantPayoutFileTaxCalculations from 'config/filters/merchantPayouts/fileTaxCalculations.json';
import transactionsMerchantPayoutIncrementsPivot from 'config/filters/transactionsMerchantPayoutIncrementsPivot.json';
import merchantPayoutIncrementTaxes from 'config/filters/merchantPayouts/incrementTaxes.json';
import merchantPayoutIncrements from 'config/filters/merchantPayouts/increments.json';
import merchantPayoutIncrementExchangeRates from 'config/filters/merchantPayouts/incrementExchangeRates.json';
import merchantPayoutSettlementsImportHistory from 'config/filters/merchantPayouts/settlementsImportHistory.json';
import allConsumers from 'config/filters/consumers/all.json';
import blacklistedConsumers from 'config/filters/consumers/blacklisted.json';
import whitelistedConsumers from 'config/filters/consumers/whiteListed.json';
import bankingSDK from 'config/filters/openBanking/bankingSDK.json';
import ibanXS from 'config/filters/openBanking/ibanXS.json';
import bankingSDKRecipients from 'config/filters/openBanking/bankingSDKRecipients.json';
import bankingSDKConnectorsRecipientsPivot from 'config/filters/openBanking/bankingBankingSDKConnectorsRecipientsPivot.json';
import ibanXSBanksRecipientsPivot from 'config/filters/openBanking/ibanXSBanksRecipientsPivot.json';
import ibanXSBanksCountriesPivot from 'config/filters/openBanking/ibanXSBanksCountriesPivot.json';
import activeConsumers from 'config/filters/consumers/active.json';
import manualBulkKYCsImportHistory from 'config/filters/manualBulkKYCsImportHistory.json';
import merchantUsers from 'config/filters/merchantUsers.json';
import ledgers from 'config/filters/ledgers.json';
import generalSetups from 'config/filters/setups/general.json';
import onboardingApplications from 'config/filters/onboarding/applications.json';
import waMessages from 'config/filters/waMessaging/waMessages.json';
import waMessagesSetups from 'config/filters/waMessaging/waMessagesSetups.json';
import phoneVerifications from 'config/filters/waMessaging/phoneVerifications.json';
import failedPhoneVerifications from 'config/filters/waMessaging/failedPhoneVerifications.json';
import waTemplatesSetups from 'config/filters/waMessaging/waTemplatesSetups.json';
import shipmentTracking from 'config/filters/shipmentTracking.json';

import { typed, type Config } from "ui/widgets/Filters/hooks/useFiltersConfigParser";
import InvalidTableGridModeException from "../exceptions/InvalidTableGridModeException";

const configRegistry = new Map<TableGridMode, Config[]>([
    [TableGridMode.Transaction, typed(transactions)],
    [TableGridMode.Merchant, typed(merchants)],
    [TableGridMode.User, typed(users)],
    [TableGridMode.MerchantPayoutsSummaryFiles, typed(merchantPayoutSummaryFiles)],
    [TableGridMode.MerchantPayoutsFiles, typed(merchantPayoutFiles)],
    [TableGridMode.MerchantPayoutCalculations, typed(merchantPayoutCalculations)],
    [TableGridMode.MerchantPayoutFileTaxCalculations, typed(merchantPayoutFileTaxCalculations)],
    [TableGridMode.TransactionsMerchantPayoutIncrementsPivot, typed(transactionsMerchantPayoutIncrementsPivot)],
    [TableGridMode.MerchantPayoutIncrementTaxes, typed(merchantPayoutIncrementTaxes)],
    [TableGridMode.MerchantPayoutIncrements, typed(merchantPayoutIncrements)],
    [TableGridMode.MerchantPayoutIncrementExchangeRates, typed(merchantPayoutIncrementExchangeRates)],
    [TableGridMode.MerchantPayoutsSettlementsImportHistory, typed(merchantPayoutSettlementsImportHistory)],
    [TableGridMode.Consumer, typed(allConsumers)],
    [TableGridMode.BlacklistedConsumer, typed(blacklistedConsumers)],
    [TableGridMode.WhitelistedConsumer, typed(whitelistedConsumers)],
    [TableGridMode.ActiveConsumer, typed(activeConsumers)],
    [TableGridMode.ManualBulkKYCsImportHistory, typed(manualBulkKYCsImportHistory)],
    [TableGridMode.TransactionList, typed(transactionList)],
    [TableGridMode.MerchantUser, typed(merchantUsers)],
    [TableGridMode.MerchantLedger, typed(ledgers)],
    [TableGridMode.OpenBankingBankingSDK, typed(bankingSDK)],
    [TableGridMode.OpenBankingIbanXS, typed(ibanXS)],
    [TableGridMode.OpenBankingBankingSDKRecipients, typed(bankingSDKRecipients)],
    [TableGridMode.OpenBankingBankingSDKConnectorsRecipientsPivot, typed(bankingSDKConnectorsRecipientsPivot)],
    [TableGridMode.OpenBankingIbanXSBanksRecipientsPivot, typed(ibanXSBanksRecipientsPivot)],
    [TableGridMode.OpenBankingIbanXSBanksCountriesPivot, typed(ibanXSBanksCountriesPivot)],
    [TableGridMode.GeneralSetup, typed(generalSetups)],
    [TableGridMode.OnboardingApplications, typed(onboardingApplications)],
    [TableGridMode.WaMessages, typed(waMessages)],
    [TableGridMode.WaMessagesSetups, typed(waMessagesSetups)],
    [TableGridMode.PhoneVerifications, typed(phoneVerifications)],
    [TableGridMode.FailedPhoneVerifications, typed(failedPhoneVerifications)],
    [TableGridMode.WaTemplatesSetups, typed(waTemplatesSetups)],
    [TableGridMode.ShipmentTracking, typed(shipmentTracking)]
]);

export default function useTableFiltersConfig(mode: TableGridMode) {
    if (!configRegistry.has(mode)) {
        throw new InvalidTableGridModeException(
            `Unknown table grid mode`,
            {}
        );
    }

    return configRegistry.get(mode)!;
};
